// Wave 20.26 — Global metric/statute preference.
//
// Wave 20.70 — this header used to claim it was backed by the user's
// settings on the server. It was not: only localStorage was ever
// written, which made the preference per-browser rather than
// per-user. Setting it here did nothing on the phone, and anything
// that cleared site data reset it to metric. Now it genuinely is
// account-backed, with localStorage kept as a synchronous cache so
// formatters can run during render without awaiting a fetch.

import { useEffect, useState } from 'react';

export type UnitSystem = 'metric' | 'imperial';

const KEY = 'aero.chat_units';

function read(): UnitSystem {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'imperial' ? 'imperial' : 'metric';
  } catch {
    return 'metric';
  }
}

export function useUnitSystem(): UnitSystem {
  const [value, setValue] = useState<UnitSystem>(read);
  useEffect(() => {
    // Cross-tab sync — a change on another tab bumps everyone else.
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setValue(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return value;
}

export function setUnitSystem(
  next: UnitSystem,
  pushUp?: (u: UnitSystem) => Promise<unknown>,
) {
  try {
    localStorage.setItem(KEY, next);
    // Same-tab consumers don't get 'storage' events; dispatch a
    // synthetic one so hooks re-render.
    window.dispatchEvent(new StorageEvent('storage', { key: KEY, newValue: next }));
  } catch {
    /* incognito / private mode — the account write below still works */
  }
  // The account is what makes this survive a different browser, a
  // cleared cache, or picking up the phone. A failure here is the
  // actual bug users hit, so it is logged rather than swallowed.
  if (pushUp) {
    void pushUp(next).catch((e) => {
      console.warn('[units] could not save the unit preference to the account', e);
    });
  }
}

/** Wave 20.70 — adopt the account's preference once the profile has
 *  loaded. Absent means the user has never chosen, in which case this
 *  browser's existing choice is carried up so it is not lost. */
export function adoptAccountUnitSystem(
  accountUnits: UnitSystem | null | undefined,
  pushUp?: (u: UnitSystem) => Promise<unknown>,
): void {
  if (accountUnits === 'imperial' || accountUnits === 'metric') {
    if (accountUnits !== read()) setUnitSystem(accountUnits);
    return;
  }
  // Wave 20.70a — only carry a REAL local choice up. read() returns
  // 'metric' both when the user chose it and when nothing is stored,
  // so pushing it blindly records a preference the user never
  // expressed. On mobile that bug stamped metric onto the account of
  // someone who had chosen statute.
  if (pushUp && hasExplicitLocalChoice()) void pushUp(read()).catch(() => {});
}

/** Whether this browser has a stored choice, as opposed to falling
 *  back to the default. */
function hasExplicitLocalChoice(): boolean {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'imperial' || v === 'metric';
  } catch {
    return false;
  }
}

// ---------- pure formatters ----------

export function fmtDistance(m: number | null | undefined, u: UnitSystem): string {
  if (m == null || m === 0) return '—';
  if (u === 'imperial') {
    const mi = m / 1609.344;
    if (mi >= 1) return `${mi.toFixed(2)} mi`;
    const ft = m * 3.28084;
    return `${Math.round(ft)} ft`;
  }
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${Math.round(m)} m`;
}

// Wave 20.49ai — Peaks specifically must ALWAYS render distance
// in a fraction of mi or km — never ft or m, no matter how
// short the peak is. Rider preference: "For peaks, we should
// never show the distance in ft or meters. It should be in a
// fraction of a mile or km." A 400 m peak reads as "0.25 mi"
// (imperial) or "0.40 km" (metric), a 2 km peak as "1.24 mi"
// or "2.00 km".
export function fmtPeakDistance(
  m: number | null | undefined,
  u: UnitSystem,
): string {
  if (m == null || m === 0) return '—';
  if (u === 'imperial') return `${(m / 1609.344).toFixed(2)} mi`;
  return `${(m / 1000).toFixed(2)} km`;
}

export function fmtElevation(m: number | null | undefined, u: UnitSystem): string {
  if (m == null || m === 0) return '—';
  if (u === 'imperial') return `${Math.round(m * 3.28084)} ft`;
  return `${Math.round(m)} m`;
}

// Wave 20.70 — vertical ascent rate. Its absence is why the same
// ft/h vs m/h branch was copied into four files. Input is m/h.
export function fmtVam(mPerH: number | null | undefined, u: UnitSystem): string {
  if (mPerH == null || !Number.isFinite(mPerH)) return '—';
  if (u === 'imperial') return `${Math.round(mPerH * 3.28084).toLocaleString()} ft/h`;
  return `${Math.round(mPerH).toLocaleString()} m/h`;
}

export function fmtSpeed(mps: number | null | undefined, u: UnitSystem): string {
  if (mps == null || mps === 0) return '—';
  if (u === 'imperial') return `${(mps * 2.23694).toFixed(1)} mph`;
  return `${(mps * 3.6).toFixed(1)} km/h`;
}

export function fmtPace(mps: number | null | undefined, u: UnitSystem): string {
  if (mps == null || mps <= 0.1) return '—';
  if (u === 'imperial') {
    const secPerMi = 1609.344 / mps;
    const m = Math.floor(secPerMi / 60);
    const s = Math.round(secPerMi % 60);
    return `${m}:${String(s).padStart(2, '0')} /mi`;
  }
  const secPerKm = 1000 / mps;
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')} /km`;
}

// Temperature — input is always Celsius (weather service canonical
// unit). Round to whole degrees; a tenth of a degree isn't useful in
// a chip.
export function fmtTemp(c: number | null | undefined, u: UnitSystem): string {
  if (c == null) return '—';
  if (u === 'imperial') return `${Math.round((c * 9) / 5 + 32)}°F`;
  return `${Math.round(c)}°C`;
}

// Wave 20.41 — Precipitation. Input is mm (per hour or total; caller
// picks the unit label via `suffix`). Rounds to two decimals in
// imperial so light rain (0.05 in/h) stays visible.
export function fmtPrecip(
  mm: number | null | undefined,
  u: UnitSystem,
  suffix: '/h' | ' total' | '' = '/h',
): string {
  if (mm == null || mm === 0) return '—';
  if (u === 'imperial') {
    const inches = mm * 0.0393701;
    return `${inches.toFixed(2)} in${suffix}`;
  }
  return `${mm.toFixed(1)} mm${suffix}`;
}

// Wave 20.41 — Reference speed used in aero "at N km/h" copy. Users
// on imperial should see the nearest round mph value. 40 km/h ≈ 25
// mph (24.86, rounded up because that's the industry standard for
// TT/breakaway pacing in the imperial-speaking world).
export function refAeroSpeedLabel(u: UnitSystem): string {
  return u === 'imperial' ? '25 mph' : '40 km/h';
}
export function refAeroDistanceLabel(u: UnitSystem): string {
  return u === 'imperial' ? '25 mi' : '40 km';
}

// Wave 20.42 — Signed relative headwind. Backend stores m/s along
// direction of travel (+ = headwind, - = tailwind). We render it as
// "N mph HEAD" / "N mph TAIL" / "CALM" so the value is unambiguous
// at a glance without needing a sign glyph. Threshold ~0.5 m/s
// (~1 mph / ~2 km/h) counts as calm — sub-perception noise.
export function fmtRelativeWind(
  mps: number | null | undefined,
  u: UnitSystem,
): { label: string; kind: 'head' | 'tail' | 'calm' } {
  // Wave 20.49aw — Rider asked us to stop using the 🌬️ Unicode
  // emoji anywhere weather is shown. Labels now carry text
  // only; callers prepend the branded WindStreakGlyph as an
  // inline element instead.
  if (mps == null || !Number.isFinite(mps)) return { label: '—', kind: 'calm' };
  if (Math.abs(mps) < 0.5) return { label: 'Calm', kind: 'calm' };
  const abs = Math.abs(mps);
  const magnitude =
    u === 'imperial' ? `${(abs * 2.23694).toFixed(1)} mph` : `${(abs * 3.6).toFixed(1)} km/h`;
  if (mps > 0) return { label: `${magnitude} HEAD`, kind: 'head' };
  return { label: `${magnitude} TAIL`, kind: 'tail' };
}
